import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { TokenTextInput, type TokenOption } from './TokenTextInput';

type Data = Record<string, unknown>;
type OnChange = (patch: Data) => void;

/**
 * Branch node — a real condition builder. Writes `properties.condition` in
 * the exact shape the cascade engine's evaluateBranchCondition parses
 * (`has_label:<id>`, `in_group:<id>`, `field_eq:<name>:<value>`,
 * `weekday:<a>-<b>`) and routes true/false through the fixed outcome ports.
 */
export function BranchPanel({ data, onChange }: { data: Data; onChange: OnChange }) {
  const labels = trpc.contacts.labels.list.useQuery();
  const groups = trpc.contacts.groups.list.useQuery();
  const fields = trpc.contacts.customFields.list.useQuery();

  const parsed = parseCondition(String(data.condition ?? ''));
  const [kind, setKind] = useState(parsed.kind);

  const emit = (condition: string) => onChange({ condition });

  return (
    <Stack gap="md">
      <Select
        label="Condition type"
        data={[
          { value: 'has_label', label: 'Contact has label' },
          { value: 'in_group', label: 'Contact is in group' },
          { value: 'field_eq', label: 'Custom field equals' },
          { value: 'weekday', label: 'Day of week is between' },
        ]}
        value={kind}
        onChange={(v) => {
          const next = (v as ConditionKind) ?? 'has_label';
          setKind(next);
          emit(''); // clear until the operand is chosen
        }}
      />

      {kind === 'has_label' && (
        <Select
          label="Label"
          placeholder="Choose a label"
          searchable
          data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
          value={parsed.kind === 'has_label' ? parsed.a : null}
          onChange={(v) => emit(v ? `has_label:${v}` : '')}
        />
      )}
      {kind === 'in_group' && (
        <Select
          label="Group"
          placeholder="Choose a group"
          searchable
          data={(groups.data ?? []).map((g) => ({ value: String(g.id), label: g.title }))}
          value={parsed.kind === 'in_group' ? parsed.a : null}
          onChange={(v) => emit(v ? `in_group:${v}` : '')}
        />
      )}
      {kind === 'field_eq' && (
        <Stack gap="xs">
          <Select
            label="Custom field"
            placeholder="Choose a field"
            searchable
            data={(fields.data ?? []).map((f) => ({ value: f.inputName, label: f.inputName }))}
            value={parsed.kind === 'field_eq' ? parsed.a : null}
            onChange={(v) =>
              emit(v ? `field_eq:${v}:${parsed.kind === 'field_eq' ? parsed.b : ''}` : '')
            }
          />
          <TextInput
            label="Equals value"
            value={parsed.kind === 'field_eq' ? parsed.b : ''}
            onChange={(e) =>
              emit(
                parsed.kind === 'field_eq' && parsed.a
                  ? `field_eq:${parsed.a}:${e.currentTarget.value}`
                  : '',
              )
            }
          />
        </Stack>
      )}
      {kind === 'weekday' && (
        <Group grow>
          <Select
            label="From"
            data={WEEKDAYS}
            value={parsed.kind === 'weekday' ? parsed.a : '1'}
            onChange={(v) =>
              emit(`weekday:${v ?? '1'}-${parsed.kind === 'weekday' ? parsed.b : '5'}`)
            }
          />
          <Select
            label="To"
            data={WEEKDAYS}
            value={parsed.kind === 'weekday' ? parsed.b : '5'}
            onChange={(v) =>
              emit(`weekday:${parsed.kind === 'weekday' ? parsed.a : '1'}-${v ?? '5'}`)
            }
          />
        </Group>
      )}

      <Alert color="indigo" variant="light" icon={<IconInfoCircle size={16} />}>
        Connect the <b>Yes</b> port when the condition is met, and <b>No</b> otherwise.
      </Alert>
    </Stack>
  );
}

/** Tag node — attach a real contact label. */
export function TagPanel({ data, onChange }: { data: Data; onChange: OnChange }) {
  const labels = trpc.contacts.labels.list.useQuery();
  return (
    <Stack gap="md">
      <Select
        label="Apply label"
        placeholder="Choose a label"
        searchable
        data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
        value={data.labelId != null ? String(data.labelId) : null}
        onChange={(v) => {
          const label = (labels.data ?? []).find((l) => String(l.id) === v);
          onChange({
            labelId: v ? Number(v) : undefined,
            labelTitle: label?.title,
            // Drop the legacy freeform tag once a real label is chosen.
            tag: undefined,
          });
        }}
      />
      {data.tag != null && data.labelId == null && (
        <Text size="xs" c="dimmed">
          Legacy tag "{String(data.tag)}" — re-select a label above to bind it.
        </Text>
      )}
    </Stack>
  );
}

/** Capture node — save the answer to a real custom field (creatable). */
export function CapturePanel({
  data,
  onChange,
  tokenOptions,
}: {
  data: Data;
  onChange: OnChange;
  tokenOptions: TokenOption[];
}) {
  const utils = trpc.useUtils();
  const fields = trpc.contacts.customFields.list.useQuery();
  const createField = trpc.contacts.customFields.create.useMutation();
  const [newField, setNewField] = useState('');

  const options = useMemo(
    () => (fields.data ?? []).map((f) => ({ value: f.inputName, label: f.inputName })),
    [fields.data],
  );

  const handleCreate = () => {
    const name = newField.trim();
    if (!name) return;
    createField.mutate(
      { inputName: name, inputType: 'text' },
      {
        onSuccess: () => {
          void utils.contacts.customFields.list.invalidate();
          onChange({ variable: name });
          setNewField('');
          notifications.show({ message: `Field "${name}" created`, color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="md">
      <TokenTextInput
        label="Question text"
        placeholder="e.g. What's your budget?"
        multiline
        value={String(data.replyText ?? '')}
        onChange={(v) => onChange({ replyText: v })}
        options={tokenOptions}
      />
      <Select
        label="Save answer to field"
        description="Referenced later as {{field.<name>}}."
        placeholder="Choose a field"
        searchable
        data={options}
        value={String(data.variable ?? '') || null}
        onChange={(v) => onChange({ variable: v ?? undefined })}
      />
      <Group gap="xs" align="flex-end">
        <TextInput
          flex={1}
          size="xs"
          label="…or create a new field"
          placeholder="e.g. budget"
          value={newField}
          onChange={(e) => setNewField(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
        />
        <Button
          size="xs"
          variant="light"
          loading={createField.isPending}
          disabled={!newField.trim()}
          onClick={handleCreate}
        >
          Create
        </Button>
      </Group>
    </Stack>
  );
}

/** Interactive node — buttons OR a single-select list. */
export function InteractivePanel({
  data,
  onChange,
  tokenOptions,
}: {
  data: Data;
  onChange: OnChange;
  tokenOptions: TokenOption[];
}) {
  const mode = String(data.interactiveMode ?? 'buttons').toLowerCase();
  const buttons = (data.buttons as string[] | undefined) ?? [];
  const rows = (data.rows as Array<{ title?: string; description?: string }> | undefined) ?? [];

  return (
    <Stack gap="md">
      <SegmentedControl
        fullWidth
        data={[
          { value: 'buttons', label: 'Buttons (max 3)' },
          { value: 'list', label: 'List (max 10)' },
        ]}
        value={mode}
        onChange={(v) => onChange({ interactiveMode: v })}
      />

      <TokenTextInput
        label="Message text"
        placeholder="e.g. How can we help you today?"
        multiline
        value={String(data.replyText ?? '')}
        onChange={(v) => onChange({ replyText: v })}
        options={tokenOptions}
      />

      {mode === 'list' ? (
        <Stack gap="xs">
          <TextInput
            label="List button label"
            description="Opens the list (max 20 chars)"
            maxLength={20}
            value={String(data.listButtonText ?? '')}
            placeholder="Choose"
            onChange={(e) => onChange({ listButtonText: e.currentTarget.value })}
          />
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Rows
            </Text>
            <Badge size="xs" variant="light">
              {rows.length}/10
            </Badge>
          </Group>
          {rows.map((r, i) => (
            <Group key={i} gap="xs" align="flex-start">
              <Stack gap={2} flex={1}>
                <TextInput
                  size="xs"
                  placeholder={`Row ${i + 1} title`}
                  maxLength={24}
                  value={r.title ?? ''}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], title: e.currentTarget.value };
                    onChange({ rows: next });
                  }}
                />
                <TextInput
                  size="xs"
                  placeholder="Description (optional)"
                  maxLength={72}
                  value={r.description ?? ''}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], description: e.currentTarget.value };
                    onChange({ rows: next });
                  }}
                />
              </Stack>
              <ActionIcon
                color="red"
                variant="subtle"
                size="sm"
                onClick={() => onChange({ rows: rows.filter((_, idx) => idx !== i) })}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
          {rows.length < 10 && (
            <Button
              size="xs"
              variant="light"
              onClick={() => onChange({ rows: [...rows, { title: '' }] })}
            >
              Add row
            </Button>
          )}
        </Stack>
      ) : (
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Buttons
            </Text>
            <Badge size="xs" variant="light" color="green">
              {buttons.length}/3
            </Badge>
          </Group>
          {buttons.map((btn, i) => (
            <Group key={i} gap="xs">
              <TextInput
                flex={1}
                size="xs"
                maxLength={20}
                placeholder={`Button ${i + 1} label`}
                value={btn}
                onChange={(e) => {
                  const next = [...buttons];
                  next[i] = e.currentTarget.value;
                  onChange({ buttons: next });
                }}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                size="sm"
                onClick={() => onChange({ buttons: buttons.filter((_, idx) => idx !== i) })}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
          {buttons.length < 3 && (
            <Button
              size="xs"
              variant="light"
              color="green"
              onClick={() => onChange({ buttons: [...buttons, 'New Button'] })}
            >
              Add button
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

// ── condition parsing helpers ────────────────────────────────────────
type ConditionKind = 'has_label' | 'in_group' | 'field_eq' | 'weekday';
type ParsedCondition =
  | { kind: 'has_label'; a: string | null }
  | { kind: 'in_group'; a: string | null }
  | { kind: 'field_eq'; a: string | null; b: string }
  | { kind: 'weekday'; a: string; b: string };

const WEEKDAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

function parseCondition(condition: string): ParsedCondition {
  if (condition.startsWith('has_label:'))
    return { kind: 'has_label', a: condition.slice('has_label:'.length) || null };
  if (condition.startsWith('in_group:'))
    return { kind: 'in_group', a: condition.slice('in_group:'.length) || null };
  if (condition.startsWith('field_eq:')) {
    const [, name, ...rest] = condition.split(':');
    return { kind: 'field_eq', a: name || null, b: rest.join(':') };
  }
  if (condition.startsWith('weekday:')) {
    const [a, b] = condition.slice('weekday:'.length).split('-');
    return { kind: 'weekday', a: a || '1', b: b || '5' };
  }
  return { kind: 'has_label', a: null };
}
